import React, { ReactNode } from 'react';
import { ClassNames } from '@emotion/react';
import classnames from 'classnames';

import ReactModal, { Props } from 'react-modal';
import { IconSvg } from '../../common/IconSvg';

import {
  // Styles for Modal
  modalOverlay,
  modalOverlayAfterOpen,
  modalOverlayBeforeClose,
  modalContent,
  modalContentAfterOpen,
  modalContentBeforeClose,
  modalContentSmall,
  modalContentMedium,
  modalContentBig,
  modalBodyOpen,

  // Modal styles for content
  ModalPreheader,
  Header,
  Title,
  Content,
  WrapperScroll,
  WrapperScrollInner,
  Form,
  FieldsWrapper,
  Paragraph,
  ParagraphLink,
  Footer,
  FooterScroll,
  ButtonCloseModal,
  Centred
} from './styles';
import { getRem, getScrollbarWidth } from '../../utils';

export interface ModalProps {
  isOpen?: boolean;
  title?: ReactNode;
  footer?: ReactNode;
  isClosable?: boolean;
  preheader?: string;
  size?: 'small' | 'medium' | 'big' | number;
  padding?: string;
  classNameModal?: string;
}

export interface ModalState {
  isOpen: boolean;
}

interface PropsWrapperScroll {
  children: ReactNode;
}

const ContentScroll = (props: PropsWrapperScroll) => (
  <WrapperScroll>
    <WrapperScrollInner>{props.children}</WrapperScrollInner>
  </WrapperScroll>
);

export const CLOSE_TIMEOUT_MS = 200;

export class Modal extends React.Component<Props & ModalProps, ModalState> {
  static Title = Title;
  static Header = Header;
  static Content = Content;
  static ContentScroll = ContentScroll;
  static Form = Form;
  static FieldsWrapper = FieldsWrapper;
  static Footer = Footer;
  static FooterScroll = FooterScroll;
  static Paragraph = Paragraph;
  static ParagraphLink = ParagraphLink;
  static Centred = Centred;

  static defaultProps = {
    isOpen: true,
    isClosable: true,
    size: 'small'
  };

  state: ModalState = {
    isOpen: this.props.isOpen
  };

  timeoutClose?: number;

  setPaddingBody = () => {
    const body = document.body;
    if (body.offsetHeight < body.scrollHeight) {
      body.style.paddingRight = `${getScrollbarWidth()}px`;
    }
  };

  resetPaddingBody = () => {
    document.body.style.paddingRight = '';
  };

  getBaseClass = () => {
    switch (this.props.size) {
      case 'small':
        return modalContentSmall;

      case 'medium':
        return modalContentMedium;

      case 'big':
        return modalContentBig;

      default:
        return modalContentMedium;
    }
  };

  onRequestCloseCustom = () => {
    this.setState({ isOpen: false });
    this.timeoutClose =
      this.props.onRequestClose && setTimeout(this.props.onRequestClose, CLOSE_TIMEOUT_MS);
  };

  componentWillUnmount() {
    clearTimeout(this.timeoutClose);
  }

  public render() {
    // if (SSR) {
    //   return null;
    // }

    const {
      preheader,
      title,
      children,
      footer,
      size,
      padding,
      isClosable,
      classNameModal,
      ...other
    } = this.props;

    const getStyle = {
      content: {
        maxWidth: getRem(size),
        padding: preheader ? '0 5rem 5rem' : padding
      }
    };

    return (
      <ClassNames>
        {({ css }) => {
          return (
            <ReactModal
              className={{
                base: classnames(css(modalContent), {
                  [css(modalContentSmall)]: size === 'small',
                  [css(modalContentMedium)]: size === 'medium',
                  [css(modalContentBig)]: size === 'big',
                  [css(classNameModal)]: !!classNameModal
                }),
                afterOpen: css(modalContentAfterOpen),
                beforeClose: css(modalContentBeforeClose)
              }}
              overlayClassName={{
                base: css(modalOverlay),
                afterOpen: css(modalOverlayAfterOpen),
                beforeClose: css(modalOverlayBeforeClose)
              }}
              bodyOpenClassName={css(modalBodyOpen)}
              closeTimeoutMS={CLOSE_TIMEOUT_MS}
              style={getStyle}
              ariaHideApp={false}
              {...other}
              isOpen={this.state.isOpen}
              onRequestClose={this.onRequestCloseCustom}
              onAfterOpen={this.setPaddingBody}
              onAfterClose={this.resetPaddingBody}
            >
              {preheader && <ModalPreheader>{preheader}</ModalPreheader>}
              {title && (
                <Header>{typeof title === 'string' ? <Title>{title}</Title> : title}</Header>
              )}

              <Content>{children}</Content>

              {footer && <Footer>{footer}</Footer>}

              {isClosable && (
                <ButtonCloseModal
                  view="transparent"
                  shape="circle"
                  type="button"
                  aria-label="Close"
                  onClick={this.onRequestCloseCustom}
                  preheader={preheader}
                  data-testid="modalCloseBtn"
                >
                  <IconSvg src="close" />
                </ButtonCloseModal>
              )}
            </ReactModal>
          );
        }}
      </ClassNames>
    );
  }
}
